import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { theme } from '../../configs/theme';
import { ReactNode } from 'react';

export interface DeleteConfirmationDialogProps {
  /**
   * Controls whether the dialog is open
   */
  open: boolean;
  /**
   * Callback fired when the dialog should be closed
   */
  onClose: () => void;
  /**
   * Callback fired when the delete action is confirmed
   */
  onConfirm: () => void;
  /**
   * The title of the dialog
   */
  title: string;
  /**
   * The main message to display in the dialog
   */
  message: string | ReactNode;
  /**
   * Optional secondary message (usually the warning text)
   * @default "Denna åtgärd kan inte ångras."
   */
  warningMessage?: string;
  /**
   * Text for the confirm button
   * @default "Ta bort"
   */
  confirmButtonText?: string;
}

/**
 * A reusable delete confirmation dialog component.
 *
 * @example
 * ```tsx
 * const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
 *
 * <DeleteConfirmationDialog
 *   open={showDeleteConfirmation}
 *   onClose={() => setShowDeleteConfirmation(false)}
 *   onConfirm={handleDelete}
 *   title="Ta bort ärende"
 *   message={`Är du säker på att du vill ta bort ärende #${caseNumber}?`}
 * />
 * ```
 */
export const DeleteConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  warningMessage = 'Denna åtgärd kan inte ångras.',
  confirmButtonText = 'Ta bort',
}: DeleteConfirmationDialogProps) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mt: 2 }}>
          {message}
        </Typography>
        {warningMessage && (
          <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.secondary }}>
            {warningMessage}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: '1rem 1.5rem' }}>
        <Button onClick={onConfirm} variant="contained" color="error">
          {confirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
